import type { UserStatePath } from "../types/deltas";
import type { MessageWindowItem } from "../types/messages";
import type { UserState } from "../types/state";

export class PathHelpers {
  public static getMessageListPath = (
    state: UserState,
    chatIdOverride?: string,
  ): UserStatePath => {
    const chatId = chatIdOverride ?? state.current_chat_id;

    if (!chatId || !state.chats[chatId]) {
      throw Error(`Chat not found: ${chatId ?? "null"}`);
    }

    return `user_state.chats.${chatId}.messages`;
  };

  /**
   * Classifies a delta path that writes *into* a chat's fields. Returns null for
   * paths that don't target a chat's internals — the whole `chats` map, or a
   * whole-chat `value_set` (`user_state.chats.<id>`) that legitimately
   * creates/replaces a chat. Otherwise returns:
   *  - `chatId`
   *  - `intoMessageList`: the path writes into `messages` (e.g. `messages.<idx>`
   *    or `messages.<idx>.<field>`)
   *  - `messageIndex`: the numeric `<idx>`, or null when the message segment is
   *    absent/non-numeric
   *  - `nestedUnderMessage`: a field follows the index (`messages.<idx>.<field>`),
   *    so the message at `<idx>` must already exist
   *
   * Callers use this to refuse fabricating a chat that isn't loaded and to reject
   * out-of-bounds message writes that would null-pad the array (ADO-1467).
   */
  public static parseChatWrite = (
    path: UserStatePath,
  ): {
    chatId: string;
    intoMessageList: boolean;
    messageIndex: number | null;
    nestedUnderMessage: boolean;
  } | null => {
    const parts = path.split(".");
    if (
      parts[0] !== "user_state" ||
      parts[1] !== "chats" ||
      parts.length <= 3
    ) {
      return null;
    }
    const intoMessageList = parts[3] === "messages" && parts.length > 4;
    const parsedIndex = intoMessageList ? Number.parseInt(parts[4], 10) : NaN;
    return {
      chatId: parts[2],
      intoMessageList,
      messageIndex: Number.isNaN(parsedIndex) ? null : parsedIndex,
      nestedUnderMessage: intoMessageList && parts.length > 5,
    };
  };

  public static findMessageIndex = (
    state: UserState,
    basePath: UserStatePath,
    messageId: string,
  ): number => {
    const { value: messageList } = PathHelpers.followPath(state, basePath);
    if (!Array.isArray(messageList)) {
      return -1;
    }
    return (messageList as MessageWindowItem[]).findIndex(
      (m) => m.id === messageId,
    );
  };

  public static followPath = (
    state: UserState,
    path: UserStatePath,
    createMissing: boolean = false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): { value: any; parent: any; parentKey: string | number | null } => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let value: any = state;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let parent: any = null;

    let parentKey: string | number | null = null;

    const pathParts = path.split(".");

    for (let i = 0; i < pathParts.length; i++) {
      const pathPart = pathParts[i];

      if (i === 0 && pathPart === "user_state") continue;

      if (pathPart === "") continue;

      if (Array.isArray(value)) {
        const pathPartInteger = parseInt(pathPart);
        parent = value;
        parentKey = pathPartInteger;
        value = value[pathPartInteger];
      } else if (typeof value === "object" && value !== null) {
        parent = value;
        parentKey = pathPart;
        if (pathPart in value) {
          value = value[pathPart];
          if (i < pathParts.length - 1 && createMissing) {
            if (
              value === null ||
              value === undefined ||
              (typeof value !== "object" && !Array.isArray(value))
            ) {
              if (value !== null && value !== undefined) {
                console.warn(
                  `Replacing non-object value (${typeof value}) at path '${pathParts.slice(0, i + 1).join(".")}' with object`,
                );
              }
              parent[pathPart] = {};
              value = parent[pathPart];
            }
          }
        } else {
          if (createMissing && i !== pathParts.length - 1) {
            value[pathPart] = {};
            value = value[pathPart];
          } else if (i === pathParts.length - 1) {
            value = undefined;
          } else {
            throw Error(`Invalid path: ${path}`);
          }
        }
      } else if (i !== pathParts.length - 1) {
        throw Error(`Invalid path: ${path}`);
      }
    }

    return { value, parent, parentKey };
  };
}
